import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Link,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import whiteTextLogo from "../assets/images/logo_white_letters.png";
import api from "../services/api";
import { useSnackbarAlert } from "../hooks/useSnackbarAlert";

const storeLocations = [
  {
    address: "1483 Topsail Rd., Paradise, NL, A1L 1P9",
    hours: "Monday - Sunday: 9:00am - 7:00pm",
  },
  {
    address: "16 Stavanger Dr, St. John's, NL A1A 5E8",
    hours: "Mon - Sat: 8:00am - 7:00pm\nSundays: 9:00am - 7:00pm",
  },
  {
    address: "48 Kenmount Rd., St. John's, NL A1B 1W3",
    hours: "Mon - Sat: 9:30am - 9:00pm\nSundays: 12:00pm - 5:00pm",
  },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const snackbar = useSnackbarAlert();

  const validateEmail = (val) => {
    const trimmed = val.trim().toLowerCase();
    const valid = /(.+)@(.+){2,}\.(.+){2,}/.test(trimmed);
    setEmail(trimmed);
    setEmailError(!valid);
    return valid;
  };

  const handleNewsletterSignup = async () => {
    if (!validateEmail(email)) return;

    try {
      const res = await api.post("/newsletter/signup", { email });
      if (res.status === 200) {
        snackbar.success("Newsletter signup is successful!");
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      snackbar.error(`Signup was not successful! ${message}`);
    }
  };

  return (
    <Box
      className="site-footer"
      sx={{
        backgroundColor: "#f56958",
        color: "#fff",
        py: 6,
        zIndex: 10,
        mt: 5,
      }}>
      {" "}
      <Box
        sx={{ backgroundColor: "#f56958", color: "#fff", py: 6, zIndex: 10 }}>
        <Container maxWidth="xl">
          {/* Top Nav */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}>
            <Box sx={{ width: 150, mb: { xs: 3, sm: 0 } }}>
              <img src={whiteTextLogo} alt="CakeyHand Logo" width="100%" />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-end" },
                fontFamily: "Inter",
              }}>
              {["Home", "FAQs", "Contact"].map((label, i) => (
                <Link
                  key={i}
                  href={`/${label.replace(/\s+/g, "-").toLowerCase()}`}
                  color="inherit"
                  underline="none">
                  {label}
                </Link>
              ))}
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: "#fff", mb: 4 }} />

          {/* Newsletter */}
          <Box
            sx={{
              backgroundColor: "#f9d8d4",
              borderRadius: 2,
              p: 4,
              mb: 6,
            }}>
            <Typography
              variant="h4"
              sx={{
                color: "#515151",
                fontWeight: "bold",
                fontFamily: "Inter",
                fontSize: {
                  xs: "1.5rem", // ~24px
                  sm: "1.75rem",
                  md: "2rem", // default h4 size
                },
              }}>
              Join our Sweet Circle 🍰
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: "#515151",
                mb: 3,
                fontFamily: "Inter",
                fontSize: {
                  xs: "1rem", // ~16px
                  sm: "1.125rem",
                  md: "1.25rem",
                },
              }}>
              Be the first to know about new cakes, secret drops, and limited
              offers!
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: 2,
              }}>
              <TextField
                placeholder="Email address"
                error={emailError}
                helperText={emailError ? "Invalid email format" : ""}
                onChange={(e) => validateEmail(e.target.value)}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  maxWidth: 300,
                  width: "100%",
                }}
              />
              <Button
                variant="contained"
                onClick={handleNewsletterSignup}
                sx={{
                  backgroundColor: "#f56958",
                  color: "#fff",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": {
                    backgroundColor: "#fd7762cc",
                  },
                }}>
                Subscribe Now
              </Button>
            </Box>

            <Box sx={{ display: "flex", mt: 3 }}>
              <IconButton
                aria-label="Facebook"
                component="a"
                href="https://www.facebook.com/CakeyHand"
                target="_blank"
                rel="noopener"
                sx={{ color: "#f56958", backgroundColor: "#fff", mr: 2 }}>
                <FacebookIcon />
              </IconButton>
              <IconButton
                aria-label="Instagram"
                component="a"
                href="https://www.instagram.com/cakeyhand/"
                target="_blank"
                rel="noopener"
                sx={{ color: "#f56958", backgroundColor: "#fff" }}>
                <InstagramIcon />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: "#515151",
                fontFamily: "Inter",
                fontSize: {
                  xs: "0.875rem", // ~14px
                  sm: "0.9rem",
                  md: "1rem", // ~16px
                },
              }}>
              Our monthly newsletters include new products, limited-run drops,
              and first dibs on seasonal launches.
            </Typography>
          </Box>

          {/* Store Info */}
          <Grid container spacing={4}>
            {storeLocations.map((loc, idx) => (
              <Grid key={idx} item xs={12} sm={4}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Inter", fontWeight: "bold" }}>
                  Address:
                </Typography>
                <Typography variant="body1" fontFamily="Inter">
                  {loc.address}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ mt: 2, fontWeight: "bold", fontFamily: "Inter" }}>
                  Hours:
                </Typography>
                <Typography
                  variant="body1"
                  fontFamily="Inter"
                  style={{ whiteSpace: "pre-line" }}>
                  {loc.hours}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ backgroundColor: "#fff", my: 4 }} />

          {/* Bottom */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: { xs: "center", sm: "left" },
              width: "100%",
              gap: { xs: 1, sm: 0 },
            }}>
            <Typography variant="subtitle1" sx={{ color: "#fff" }}>
              © {new Date().getFullYear()} CakeyHand Dessert Factory Inc.
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Link
                href="/terms-of-service"
                underline="always"
                color="inherit"
                target="_blank"
                rel="noopener">
                Terms of Service
              </Link>
              <Link
                href="/privacy-policy"
                underline="always"
                color="inherit"
                target="_blank"
                rel="noopener">
                Privacy Policy
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
